/**
 * TanStack Query option factories for the Platform Console.
 *
 * Convention (mirrors ``features/workspace/hooks/useCalendar.ts``):
 * route loaders call ``queryClient.ensureQueryData(xxxQueryOptions())``
 * fire-and-forget; components call ``useQuery(xxxQueryOptions())`` and get
 * the warmed cache. Mutations invalidate via the ``consoleQueryKeys``
 * hierarchy.
 */

import { queryOptions } from "@tanstack/react-query";
import type { ConsoleDocumentFilters } from "./client";

import { flowsApiQueryKeys } from "@/features/hive-flows/api/FlowsApiContext";

import { CONSOLE_FLOWS_SCOPE } from "../flows/consoleFlowsApi";
import {
  fetchConsoleBillingSummary,
  fetchConsoleConnectorDetail,
  fetchConsoleConnectorKinds,
  fetchConsoleConnectorSyncs,
  fetchConsoleConnectors,
  fetchConsoleContext,
  fetchConsoleCredits,
  fetchConsoleDocumentContent,
  fetchConsoleDocumentJobs,
  fetchConsoleDocuments,
  fetchConsoleFlowDetail,
  fetchConsoleFlowStats,
  fetchConsoleFlowTemplates,
  fetchConsoleFlows,
  fetchConsoleInvoices,
  fetchConsoleJobs,
  fetchConsoleKeyAudit,
  fetchConsoleKeyScopes,
  fetchConsoleKeyUsage,
  fetchConsoleKeys,
  fetchConsoleMemberRoles,
  fetchConsoleMembers,
  fetchConsoleModels,
  fetchConsoleOverview,
  fetchConsolePersonalConnections,
  fetchConsoleQuotas,
  fetchConsoleSources,
  fetchConsoleUks,
  fetchConsoleUsage,
  fetchConsoleUsageBreakdown,
  fetchConsoleUsageByKey,
  fetchConsoleWalletScope,
  fetchConsoleWebhooks,
  getConsoleFlowPalette,
  getConsolePendingApprovals,
  getConsoleSourceSchema,
  getConsoleWebhookDeliveries,
  getCustomConnector,
  listActionRequests,
  listConnectorActions,
  listCustomConnectors,
} from "./client";
import type { ConsoleJobStatus, ConsoleUsageWindow } from "./types";

export const consoleQueryKeys = {
  all: ["console"] as const,
  /** Caller's org-role capability tier — gates the in-console Account group. */
  context: () => [...consoleQueryKeys.all, "context"] as const,
  walletScope: () => [...consoleQueryKeys.all, "wallet-scope"] as const,
  overview: () => [...consoleQueryKeys.all, "overview"] as const,
  keys: () => [...consoleQueryKeys.all, "keys"] as const,
  keyAudit: (keyId: string) => [...consoleQueryKeys.keys(), keyId, "audit"] as const,
  /** One key's per-window usage breakdown (billing view, carries cost). */
  keyUsage: (keyId: string, window: string) =>
    [...consoleQueryKeys.keys(), keyId, "usage", window] as const,
  keyScopes: () => [...consoleQueryKeys.all, "key-scopes"] as const,
  connectors: () => [...consoleQueryKeys.all, "connectors"] as const,
  /** Connections owned by the signed-in Console member, never the shared catalog. */
  personalConnections: () => [...consoleQueryKeys.all, "personal-connections"] as const,
  /** Root for the project's own Push-API connectors — invalidate after register/disable. */
  customConnectors: () => [...consoleQueryKeys.all, "custom-connectors"] as const,
  customConnector: (slug: string) => [...consoleQueryKeys.customConnectors(), slug] as const,
  /** Actions a connector declares — invalidate after declare/delete. */
  connectorActions: (slug: string) =>
    [...consoleQueryKeys.customConnector(slug), "actions"] as const,
  /** Test-run action requests for a connector — invalidate after propose/execute. */
  actionRequests: (slug: string) =>
    [...consoleQueryKeys.customConnector(slug), "action-requests"] as const,
  connectorKinds: () => [...consoleQueryKeys.all, "connector-kinds"] as const,
  connectorSyncs: (slug: string) => [...consoleQueryKeys.customConnector(slug), "syncs"] as const,
  /** Registered webhook endpoints — invalidate after create/delete. */
  webhooks: () => [...consoleQueryKeys.all, "webhooks"] as const,
  /** One endpoint's delivery history — invalidate after a replay. */
  webhookDeliveries: (webhookId: string) =>
    [...consoleQueryKeys.webhooks(), webhookId, "deliveries"] as const,
  /** Playground scope discovery — what the current project/key can query. */
  sources: () => [...consoleQueryKeys.all, "sources"] as const,
  sourcesForKey: (keyId: string) => [...consoleQueryKeys.sources(), "as-key", keyId] as const,
  /** One structured source's profiled K-Schema — invalidate after curate/publish. */
  sourceSchema: (sourceId: string) => [...consoleQueryKeys.sources(), sourceId, "schema"] as const,
  uks: () => [...consoleQueryKeys.all, "uks"] as const,
  connectorDetail: (connectionId: string) =>
    [...consoleQueryKeys.connectors(), connectionId] as const,
  /** Root for ALL documents state (every page + job histories) — invalidate this after upload/delete. */
  documentsRoot: () => [...consoleQueryKeys.all, "documents"] as const,
  documents: (page: number, filters: ConsoleDocumentFilters = {}) =>
    [...consoleQueryKeys.documentsRoot(), page, filters] as const,
  documentJobs: (documentId: string) =>
    [...consoleQueryKeys.documentsRoot(), "jobs", documentId] as const,
  documentContent: (documentId: string) =>
    [...consoleQueryKeys.documentsRoot(), "content", documentId] as const,
  jobs: (status: ConsoleJobStatus | "all") => [...consoleQueryKeys.all, "jobs", status] as const,
  usage: (window: string) => [...consoleQueryKeys.all, "usage", window] as const,
  usageBreakdown: (window: string) => [...consoleQueryKeys.all, "usage-breakdown", window] as const,
  /** All-keys usage ranking for a window. */
  usageByKey: (window: string) => [...consoleQueryKeys.all, "usage-by-key", window] as const,
  /** Per-key rate-limit + spend-cap visibility. */
  quotas: () => [...consoleQueryKeys.all, "quotas"] as const,
  /** Billing header snapshot (mode, estimate, last invoice). */
  billingSummary: () => [...consoleQueryKeys.all, "billing-summary"] as const,
  /** Platform-usage invoice history. */
  invoices: () => [...consoleQueryKeys.all, "invoices"] as const,
  /** Prepaid credit posture + grants. */
  credits: () => [...consoleQueryKeys.all, "credits"] as const,
  /** Team roster + pending invites + seat usage — invalidate after any member write. */
  members: () => [...consoleQueryKeys.all, "members"] as const,
  /** Assignable role catalog (static). */
  memberRoles: () => [...consoleQueryKeys.all, "member-roles"] as const,
  /** Per-purpose model config + BYOK — invalidate after any models write. */
  models: () => [...consoleQueryKeys.all, "models"] as const,
  /** Root for ALL flows state (every page + per-flow stats) — invalidate after a run. */
  flowsRoot: () => [...consoleQueryKeys.all, "flows"] as const,
  flows: (page: number, status: string | "all") =>
    [...consoleQueryKeys.flowsRoot(), page, status] as const,
  flow: (flowId: string) => [...consoleQueryKeys.flowsRoot(), "detail", flowId] as const,
  flowStats: (flowId: string) => [...consoleQueryKeys.flowsRoot(), "stats", flowId] as const,
  /** One run's full projection — polled while an upload-triggered run executes. */
  flowRun: (runId: string) => [...consoleQueryKeys.flowsRoot(), "run", runId] as const,
  flowTemplates: () => [...consoleQueryKeys.all, "flow-templates"] as const,
};

/**
 * ``GET /context`` — the caller's org-role capability tier. Warmed by the
 * shell + the Account-group route guards; drives the in-console section gate.
 * The org role rarely changes within a session, so a longish staleTime keeps
 * it out of the per-navigation refetch path.
 */
export function consoleContextQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.context(),
    queryFn: () => fetchConsoleContext(),
    staleTime: 5 * 60 * 1000,
  });
}

export function consoleOverviewQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.overview(),
    queryFn: () => fetchConsoleOverview(),
  });
}

export function consoleKeysQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.keys(),
    queryFn: () => fetchConsoleKeys(),
  });
}

export function consoleKeyAuditQueryOptions(keyId: string) {
  return queryOptions({
    queryKey: consoleQueryKeys.keyAudit(keyId),
    queryFn: () => fetchConsoleKeyAudit(keyId),
  });
}

export function consoleKeyScopesQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.keyScopes(),
    queryFn: () => fetchConsoleKeyScopes(),
    // Static vocabulary — no point refetching per wizard open.
    staleTime: 5 * 60 * 1000,
  });
}

export function consoleConnectorsQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.connectors(),
    queryFn: () => fetchConsoleConnectors(),
  });
}

export function consolePersonalConnectionsQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.personalConnections(),
    queryFn: () => fetchConsolePersonalConnections(),
  });
}

export function consoleConnectorDetailQueryOptions(connectionId: string) {
  return queryOptions({
    queryKey: consoleQueryKeys.connectorDetail(connectionId),
    queryFn: () => fetchConsoleConnectorDetail(connectionId),
  });
}

export function consoleCustomConnectorsQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.customConnectors(),
    queryFn: () => listCustomConnectors(),
  });
}

export function consoleCustomConnectorQueryOptions(slug: string) {
  return queryOptions({
    queryKey: consoleQueryKeys.customConnector(slug),
    queryFn: () => getCustomConnector(slug),
  });
}

export function consoleConnectorActionsQueryOptions(slug: string) {
  return queryOptions({
    queryKey: consoleQueryKeys.connectorActions(slug),
    queryFn: () => listConnectorActions(slug),
  });
}

export function consoleActionRequestsQueryOptions(slug: string, limit = 20) {
  return queryOptions({
    queryKey: consoleQueryKeys.actionRequests(slug),
    queryFn: () => listActionRequests(slug, limit),
  });
}

export function consoleConnectorKindsQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.connectorKinds(),
    queryFn: () => fetchConsoleConnectorKinds(),
  });
}

export function consoleConnectorSyncsQueryOptions(slug: string, limit = 20) {
  return queryOptions({
    queryKey: consoleQueryKeys.connectorSyncs(slug),
    queryFn: () => fetchConsoleConnectorSyncs(slug, limit),
    // Live-ish history: the dev pushing from a terminal sees the run appear
    // without reloading. Ticks only while the browser tab is focused (the
    // TanStack default) and only while the Storico tab is mounted.
    refetchInterval: 10_000,
  });
}

export function consoleWebhooksQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.webhooks(),
    queryFn: () => fetchConsoleWebhooks(),
  });
}

/** Fetched LAZILY — the panel mounts this only for an expanded history row.
 * Ticks while expanded (same cadence as the sync history) so a replayed
 * delivery's pending → delivered transition shows up without a reload. */
export function consoleWebhookDeliveriesQueryOptions(webhookId: string) {
  return queryOptions({
    queryKey: consoleQueryKeys.webhookDeliveries(webhookId),
    queryFn: () => getConsoleWebhookDeliveries(webhookId),
    refetchInterval: 10_000,
  });
}

export function consoleDocumentsQueryOptions(page: number, filters: ConsoleDocumentFilters = {}) {
  return queryOptions({
    queryKey: consoleQueryKeys.documents(page, filters),
    queryFn: () => fetchConsoleDocuments(page, filters),
  });
}

export function consoleDocumentJobsQueryOptions(documentId: string) {
  return queryOptions({
    queryKey: consoleQueryKeys.documentJobs(documentId),
    queryFn: () => fetchConsoleDocumentJobs(documentId),
  });
}

export function consoleDocumentContentQueryOptions(documentId: string) {
  return queryOptions({
    queryKey: consoleQueryKeys.documentContent(documentId),
    queryFn: () => fetchConsoleDocumentContent(documentId),
  });
}

export function consoleJobsQueryOptions(status?: ConsoleJobStatus) {
  return queryOptions({
    queryKey: consoleQueryKeys.jobs(status ?? "all"),
    queryFn: () => fetchConsoleJobs(status),
  });
}

export function consoleInvoicesQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.invoices(),
    queryFn: () => fetchConsoleInvoices(),
    staleTime: 60_000,
  });
}

export function consoleCreditsQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.credits(),
    queryFn: () => fetchConsoleCredits(),
    staleTime: 30_000,
  });
}

export function consoleWalletScopeQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.walletScope(),
    queryFn: () => fetchConsoleWalletScope(),
    staleTime: 30_000,
  });
}

/** ``GET /billing/summary`` — the console header snapshot (mode + estimate). */
export function consoleBillingSummaryQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.billingSummary(),
    queryFn: () => fetchConsoleBillingSummary(),
    // The user can change cards and accounting details in the external Stripe
    // portal. Always reconcile with Stripe when they return to this screen.
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
}

/** ``GET /quotas`` — per-key rate-limit + spend-cap posture. */
export function consoleQuotasQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.quotas(),
    queryFn: () => fetchConsoleQuotas(),
  });
}

/** ``GET /members`` — team roster, pending invites, seat usage. */
export function consoleMembersQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.members(),
    queryFn: () => fetchConsoleMembers(),
  });
}

/** ``GET /members/roles`` — the assignable role catalog (static vocabulary). */
export function consoleMemberRolesQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.memberRoles(),
    queryFn: () => fetchConsoleMemberRoles(),
    staleTime: 5 * 60 * 1000,
  });
}

export function consoleUsageQueryOptions(window: ConsoleUsageWindow = "30d") {
  return queryOptions({
    queryKey: consoleQueryKeys.usage(window),
    queryFn: () => fetchConsoleUsage(window),
  });
}

export function consoleUsageBreakdownQueryOptions(window: ConsoleUsageWindow = "30d") {
  return queryOptions({
    queryKey: consoleQueryKeys.usageBreakdown(window),
    queryFn: () => fetchConsoleUsageBreakdown(window),
  });
}

/** ``GET /usage/by-key`` — the project's AppKeys ranked by consumption. */
export function consoleUsageByKeyQueryOptions(window: ConsoleUsageWindow = "30d") {
  return queryOptions({
    queryKey: consoleQueryKeys.usageByKey(window),
    queryFn: () => fetchConsoleUsageByKey(window),
  });
}

/** ``GET /keys/{key_id}/usage`` — one key's usage breakdown; fetched only once
 * a key is actually selected. */
export function consoleKeyUsageQueryOptions(keyId: string, window: ConsoleUsageWindow = "30d") {
  return queryOptions({
    queryKey: consoleQueryKeys.keyUsage(keyId, window),
    queryFn: () => fetchConsoleKeyUsage(keyId, window),
    enabled: keyId.length > 0,
  });
}

export function consoleModelsQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.models(),
    queryFn: () => fetchConsoleModels(),
  });
}

export function consoleSourcesQueryOptions(asKeyId?: string) {
  return queryOptions({
    queryKey: asKeyId ? consoleQueryKeys.sourcesForKey(asKeyId) : consoleQueryKeys.sources(),
    queryFn: () => fetchConsoleSources(asKeyId),
    // The queryable set changes only when connectors sync — refetch is cheap
    // but not per-keystroke.
    staleTime: 60 * 1000,
  });
}

export function consoleFlowsQueryOptions(page = 1, status?: string) {
  return queryOptions({
    queryKey: consoleQueryKeys.flows(page, status ?? "all"),
    queryFn: () => fetchConsoleFlows(page, 20, status),
  });
}

export function consoleFlowTemplatesQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.flowTemplates(),
    queryFn: () => fetchConsoleFlowTemplates(),
    // Built-in gallery — static within a session, no point refetching.
    staleTime: 5 * 60 * 1000,
  });
}

export function consoleFlowDetailQueryOptions(flowId: string) {
  return queryOptions({
    queryKey: consoleQueryKeys.flow(flowId),
    queryFn: () => fetchConsoleFlowDetail(flowId),
    enabled: flowId.length > 0,
  });
}

/** Fetched LAZILY — the list page mounts this only for an expanded row. */
export function consoleFlowStatsQueryOptions(flowId: string) {
  return queryOptions({
    queryKey: consoleQueryKeys.flowStats(flowId),
    queryFn: () => fetchConsoleFlowStats(flowId),
  });
}

export function consoleUksQueryOptions() {
  return queryOptions({
    queryKey: consoleQueryKeys.uks(),
    queryFn: () => fetchConsoleUks(),
    // The real UKS is materialized out-of-band (ingestion / nightly), so it
    // changes rarely within a session.
    staleTime: 60 * 1000,
  });
}

export function consoleSourceSchemaQueryOptions(sourceId: string) {
  return queryOptions({
    queryKey: consoleQueryKeys.sourceSchema(sourceId),
    queryFn: () => getConsoleSourceSchema(sourceId),
    // Only fetch once a source is actually selected.
    enabled: sourceId.length > 0,
  });
}

// ── Flows builder surface (console-hosted editor, Aug 2026) ────────────────
//
// These two share their query keys with the seam-scoped hooks the reused
// builder pieces call (``flowsApiQueryKeys`` + ``CONSOLE_FLOWS_SCOPE``), so a
// route-loader warm here IS the cache the mounted ``usePalette`` /
// ``usePendingApprovals`` read — one cache entry per surface, never two.

export function consoleFlowPaletteQueryOptions() {
  return queryOptions({
    queryKey: flowsApiQueryKeys.palette(CONSOLE_FLOWS_SCOPE),
    queryFn: () => getConsoleFlowPalette(),
    // Registry catalog — changes only on a backend deploy (mirrors the hook).
    staleTime: 30 * 60 * 1000,
  });
}

export function consolePendingApprovalsQueryOptions() {
  return queryOptions({
    queryKey: flowsApiQueryKeys.pendingApprovals(CONSOLE_FLOWS_SCOPE),
    queryFn: () => getConsolePendingApprovals(),
  });
}
